import type { CSSProperties } from "react";

export type TurnstileFormTheme = "light" | "dark";
export type TurnstileFormWidth = "sm" | "md" | "lg" | "xl";
export type TurnstileFormLayout = "two-column" | "single-column";
export type TurnstileFormTriggerVariant = "blue" | "neutral";

export const widthPixels: Record<TurnstileFormWidth, number> = {
  sm: 480,
  md: 640,
  lg: 880,
  xl: 1040,
};

export const layoutTokens: Record<TurnstileFormLayout, CSSProperties> = {
  "two-column": {
    display: "grid",
    gridTemplateColumns: "repeat(2, 1fr)",
    gap: 16,
  },
  "single-column": {
    display: "grid",
    gridTemplateColumns: "1fr",
    gap: 14,
  },
};

export const baseLayoutTokens = {
  section: {
    display: "grid",
    gap: 16,
  } satisfies CSSProperties,

  container: {
    width: "100%",
    margin: "0 auto",
    borderRadius: 14,
    padding: 20,
  } satisfies CSSProperties,

  field: {
    display: "flex",
    flexDirection: "column",
    gap: 6,
  } satisfies CSSProperties,

  footer: {
    marginTop: 20,
    position: "relative",
  } satisfies CSSProperties,

  hiddenTurnstile: {
    position: "absolute",
    inset: 0,
    opacity: 0,
    pointerEvents: "none",
  } satisfies CSSProperties,

  contextPanel: {
    display: "grid",
    gap: 14,
    marginBottom: 18,
  } satisfies CSSProperties,

  contextImageRow: {
    display: "grid",
    gridTemplateColumns: "minmax(0, 1fr) minmax(0, 1fr)",
    gap: 16,
    alignItems: "start",
  } satisfies CSSProperties,

  contextImageWrap: {
    overflow: "hidden",
    borderRadius: 12,
    borderWidth: 1,
    borderStyle: "solid",
    maxWidth: "100%",
  } satisfies CSSProperties,

  contextImage: {
    display: "block",
    width: "100%",
    height: "auto",
    maxHeight: 220,
    objectFit: "cover",
  } satisfies CSSProperties,

  contextImageSpacer: {
    minWidth: 0,
  } satisfies CSSProperties,

  readOnlyGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))",
    gap: 10,
  } satisfies CSSProperties,

  readOnlyItem: {
    borderRadius: 10,
    borderWidth: 1,
    borderStyle: "solid",
    padding: "9px 11px",
  } satisfies CSSProperties,

  banner: {
    borderRadius: 10,
    padding: 12,
  } satisfies CSSProperties,

  bannerInner: {
    position: "relative",
    margin: "0 auto",
    paddingRight: 32,
    fontSize: 13,
    lineHeight: 1.45,
  } satisfies CSSProperties,

  dismissButton: {
    position: "absolute",
    right: 0,
    top: -4,
    border: 0,
    borderRadius: 6,
    padding: "2px 7px",
    background: "transparent",
    cursor: "pointer",
    fontSize: 18,
    lineHeight: 1,
  } satisfies CSSProperties,

  modalOverlay: {
    position: "fixed",
    inset: 0,
    zIndex: 9999,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    padding: 16,
    background: "rgba(15, 23, 42, 0.62)",
  } satisfies CSSProperties,

  modalPanel: {
    width: "100%",
    maxHeight: "calc(100vh - 32px)",
    overflowY: "auto",
    borderRadius: 16,
  } satisfies CSSProperties,

  modalHeader: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: 12,
    marginBottom: 12,
  } satisfies CSSProperties,

  modalTitle: {
    margin: 0,
    fontSize: 16,
    lineHeight: 1.3,
    fontWeight: 700,
  } satisfies CSSProperties,

  modalCloseIconButton: {
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    width: 32,
    height: 32,
    border: 0,
    borderRadius: 8,
    cursor: "pointer",
    background: "transparent",
    fontSize: 22,
    lineHeight: 1,
  } satisfies CSSProperties,

  modalActions: {
    display: "flex",
    justifyContent: "flex-end",
    marginTop: 14,
  } satisfies CSSProperties,

  triggerIcon: {
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    width: 16,
    height: 16,
    flex: "0 0 auto",
  } satisfies CSSProperties,
};

export const typographyTokens = {
  label: {
    fontSize: 13,
    lineHeight: 1.35,
    fontWeight: 600,
  } satisfies CSSProperties,

  input: {
    fontSize: 14,
    lineHeight: 1.4,
  } satisfies CSSProperties,

  textarea: {
    fontSize: 14,
    lineHeight: 1.45,
  } satisfies CSSProperties,

  error: {
    margin: 0,
    fontSize: 12,
    lineHeight: 1.35,
  } satisfies CSSProperties,

  button: {
    fontSize: 14,
    lineHeight: 1.2,
    fontWeight: 700,
  } satisfies CSSProperties,

  modalCloseButton: {
    fontSize: 13,
    lineHeight: 1.2,
    fontWeight: 700,
  } satisfies CSSProperties,

  readOnlyLabel: {
    fontSize: 11,
    lineHeight: 1.25,
    fontWeight: 700,
    textTransform: "uppercase",
    letterSpacing: "0.04em",
  } satisfies CSSProperties,

  readOnlyValue: {
    marginTop: 3,
    fontSize: 13,
    lineHeight: 1.35,
    fontWeight: 600,
    wordBreak: "break-word",
  } satisfies CSSProperties,
};

export const uiTokens = {
  input: {
    width: "100%",
    boxSizing: "border-box",
    borderRadius: 10,
    borderWidth: 1,
    borderStyle: "solid",
    padding: "9px 11px",
    outline: "none",
    transition:
      "border-color 160ms ease, box-shadow 160ms ease, background 160ms ease",
  } satisfies CSSProperties,

  textarea: {
    width: "100%",
    boxSizing: "border-box",
    minHeight: 110,
    resize: "vertical",
    borderRadius: 10,
    borderWidth: 1,
    borderStyle: "solid",
    padding: "9px 11px",
    outline: "none",
    transition:
      "border-color 160ms ease, box-shadow 160ms ease, background 160ms ease",
  } satisfies CSSProperties,

  button: {
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    border: 0,
    borderRadius: 10,
    padding: "10px 18px",
    cursor: "pointer",
    transition:
      "background 160ms ease, box-shadow 160ms ease, opacity 160ms ease",
  } satisfies CSSProperties,

  required: {
    marginLeft: 3,
    color: "#dc2626",
  } satisfies CSSProperties,
};

export const formThemeTokens: Record<
  TurnstileFormTheme,
  {
    container: CSSProperties;
    label: CSSProperties;
    input: CSSProperties;
    inputFocus: CSSProperties;
    textarea: CSSProperties;
    textareaFocus: CSSProperties;
    error: CSSProperties;
    button: CSSProperties;
    buttonDisabled: CSSProperties;
    contextImageWrap: CSSProperties;
    readOnlyItem: CSSProperties;
    readOnlyLabel: CSSProperties;
    readOnlyValue: CSSProperties;
    successBanner: CSSProperties;
    errorBanner: CSSProperties;
    successDismiss: CSSProperties;
    errorDismiss: CSSProperties;
    modalPanel: CSSProperties;
    modalCloseIconButton: CSSProperties;
    modalCloseButton: CSSProperties;
    triggerButton: Record<TurnstileFormTriggerVariant, CSSProperties>;
  }
> = {
  light: {
    container: {
      background: "#ffffff",
      color: "#111827",
      boxShadow: "0 12px 32px rgba(15, 23, 42, 0.08)",
    },
    label: {
      color: "#374151",
    },
    input: {
      background: "#f8fafc",
      borderColor: "#d8dee8",
      color: "#111827",
      boxShadow: "0 1px 2px rgba(15, 23, 42, 0.04)",
    },
    inputFocus: {
      background: "#ffffff",
      borderColor: "#3b82f6",
      boxShadow: "0 0 0 3px rgba(59, 130, 246, 0.16)",
    },
    textarea: {
      background: "#f8fafc",
      borderColor: "#d8dee8",
      color: "#111827",
      boxShadow: "0 1px 2px rgba(15, 23, 42, 0.04)",
    },
    textareaFocus: {
      background: "#ffffff",
      borderColor: "#3b82f6",
      boxShadow: "0 0 0 3px rgba(59, 130, 246, 0.16)",
    },
    error: {
      color: "#dc2626",
    },
    button: {
      background: "#2563eb",
      color: "#ffffff",
      boxShadow: "0 8px 18px rgba(37, 99, 235, 0.22)",
    },
    buttonDisabled: {
      opacity: 0.62,
      cursor: "not-allowed",
    },
    contextImageWrap: {
      borderColor: "#e5e7eb",
      background: "#f8fafc",
    },
    readOnlyItem: {
      borderColor: "#e5e7eb",
      background: "#f8fafc",
    },
    readOnlyLabel: {
      color: "#6b7280",
    },
    readOnlyValue: {
      color: "#111827",
    },
    successBanner: {
      background: "#ecfdf3",
      color: "#166534",
    },
    errorBanner: {
      background: "#fef2f2",
      color: "#991b1b",
    },
    successDismiss: {
      color: "#166534",
    },
    errorDismiss: {
      color: "#991b1b",
    },
    modalPanel: {
      background: "#ffffff",
      color: "#111827",
      boxShadow: "0 22px 60px rgba(15, 23, 42, 0.32)",
    },
    modalCloseIconButton: {
      color: "#475569",
    },
    modalCloseButton: {
      background: "#f1f5f9",
      color: "#0f172a",
      boxShadow: "none",
    },
    triggerButton: {
      blue: {
        background: "#2563eb",
        color: "#ffffff",
        boxShadow: "0 8px 18px rgba(37, 99, 235, 0.22)",
      },
      neutral: {
        background: "#f1f5f9",
        color: "#0f172a",
        boxShadow: "0 1px 3px rgba(15, 23, 42, 0.12)",
      },
    },
  },
  dark: {
    container: {
      background: "#0f172a",
      color: "#f8fafc",
      boxShadow: "0 18px 42px rgba(0, 0, 0, 0.34)",
    },
    label: {
      color: "#e2e8f0",
    },
    input: {
      background: "#1e293b",
      borderColor: "#334155",
      color: "#f8fafc",
      boxShadow: "inset 0 1px 2px rgba(0, 0, 0, 0.2)",
    },
    inputFocus: {
      background: "#1e293b",
      borderColor: "#60a5fa",
      boxShadow: "0 0 0 3px rgba(96, 165, 250, 0.18)",
    },
    textarea: {
      background: "#1e293b",
      borderColor: "#334155",
      color: "#f8fafc",
      boxShadow: "inset 0 1px 2px rgba(0, 0, 0, 0.2)",
    },
    textareaFocus: {
      background: "#1e293b",
      borderColor: "#60a5fa",
      boxShadow: "0 0 0 3px rgba(96, 165, 250, 0.18)",
    },
    error: {
      color: "#f87171",
    },
    button: {
      background: "#3b82f6",
      color: "#ffffff",
      boxShadow: "0 8px 20px rgba(59, 130, 246, 0.25)",
    },
    buttonDisabled: {
      opacity: 0.62,
      cursor: "not-allowed",
    },
    contextImageWrap: {
      borderColor: "#334155",
      background: "#1e293b",
    },
    readOnlyItem: {
      borderColor: "#334155",
      background: "rgba(30, 41, 59, 0.7)",
    },
    readOnlyLabel: {
      color: "#94a3b8",
    },
    readOnlyValue: {
      color: "#f8fafc",
    },
    successBanner: {
      background: "rgba(20, 83, 45, 0.55)",
      color: "#dcfce7",
      border: "1px solid rgba(22, 101, 52, 0.65)",
    },
    errorBanner: {
      background: "rgba(127, 29, 29, 0.55)",
      color: "#fee2e2",
      border: "1px solid rgba(153, 27, 27, 0.65)",
    },
    successDismiss: {
      color: "#dcfce7",
    },
    errorDismiss: {
      color: "#fee2e2",
    },
    modalPanel: {
      background: "#0f172a",
      color: "#f8fafc",
      boxShadow: "0 22px 60px rgba(0, 0, 0, 0.42)",
    },
    modalCloseIconButton: {
      color: "#cbd5e1",
    },
    modalCloseButton: {
      background: "#1e293b",
      color: "#f8fafc",
      boxShadow: "none",
    },
    triggerButton: {
      blue: {
        background: "#3b82f6",
        color: "#ffffff",
        boxShadow: "0 8px 20px rgba(59, 130, 246, 0.25)",
      },
      neutral: {
        background: "#1e293b",
        color: "#f8fafc",
        boxShadow: "0 1px 3px rgba(0, 0, 0, 0.24)",
      },
    },
  },
};

export function mergeStyles(
  ...styles: Array<CSSProperties | undefined | false>
): CSSProperties {
  return Object.assign({}, ...styles.filter(Boolean));
}
